/**
 * `useSSE` — hardened React hook for subscribing to a Hantavirus
 * Server-Sent Events stream.
 *
 * Replaces the previous naïve EventSource wrapper with:
 *   - Fetch-based transport (`openFetchEventSource`) so we can attach
 *     `Last-Event-ID` on every reconnect (native EventSource only sets
 *     it for its own internal retries, which we disable in favour of a
 *     smarter strategy).
 *   - Exponential back-off with jitter, capped at 30 s.
 *   - Page Visibility pause: after 30 s of tab hidden we close the
 *     connection to save battery; we reopen on visibilitychange.
 *   - Heartbeat watchdog: if no bytes arrive for 30 s (the producer
 *     emits `: ping` every 30 s), force-close and reconnect.
 *   - Reconnect counter + `status` machine surfaced to the UI for a
 *     connection banner.
 *
 * SSR-safe: the effect bails when `window`/`fetch` are unavailable.
 *
 * Channel discovery + URL composition stays out of this hook. The
 * caller passes a fully-qualified URL (typically `/sse/<channel>`).
 */
import { useEffect, useRef, useState } from "react";
import {
  openFetchEventSource,
  type FetchEventSourceHandle,
  type PolyfillEvent,
} from "#/lib/sse/event-source-polyfill.js";

/**
 * Mapping from SSE event name (the value of the `event:` line) to the
 * handler that consumes its payload. Each handler is invoked with the
 * decoded `data:` body — JSON when parseable, otherwise the raw string.
 */
export type SSEHandlers<E extends string, T> = Record<
  E,
  (data: T, event: PolyfillEvent) => void
>;

export type SSEStatus =
  /** Initial connection attempt has not yet opened. */
  | "connecting"
  /** Stream is open and receiving data (or heartbeats). */
  | "open"
  /** Stream is closed and we are waiting on a back-off timer. */
  | "reconnecting"
  /** Tab is hidden and we have closed the stream to save resources. */
  | "paused";

export interface UseSSEOptions {
  /** When false, no connection is opened (and any open one is closed). */
  enabled?: boolean;
  /**
   * If `true`, send credentials (cookies) with the SSE request. Defaults
   * to `false` because the public ticker / ship streams are unauthenticated.
   */
  withCredentials?: boolean;
  /**
   * Override the back-off schedule (ms). Defaults to:
   * `[200, 500, 1000, 2000, 5000, 10000, 20000, 30000]`. After exhausting
   * the schedule, retries continue at 30 s with `± random(0, 1000)` jitter.
   */
  backoffMs?: readonly number[];
  /** Milliseconds of producer silence before we force a reconnect. */
  heartbeatTimeoutMs?: number;
  /** Milliseconds tab must stay hidden before we close the stream. */
  hiddenTabPauseMs?: number;
  /** Custom `fetch` for tests. */
  fetchImpl?: typeof fetch;
  /**
   * Test-only hook invoked on every reconnect attempt with the URL
   * being opened. Lets tests assert on the request URL queue.
   */
  onAttempt?: (info: { url: string; lastEventId: string; attempt: number }) => void;
}

export interface UseSSEResult {
  /** True when the stream is open. */
  connected: boolean;
  /** Last error surfaced from the underlying transport. */
  error: Error | null;
  /**
   * True when the connection is in a recovery window. Equivalent to
   * `status === "reconnecting"`. Kept for backwards compatibility with
   * callers written against the previous hook surface.
   */
  reconnecting: boolean;
  /** Fine-grained connection lifecycle for the connection banner. */
  status: SSEStatus;
  /** Cumulative count of reconnects since mount; resets when the URL changes. */
  reconnectCount: number;
  /**
   * Manually trigger a reconnect immediately, skipping any pending
   * back-off timer. Used by the "Retry now" button in the banner.
   */
  retry: () => void;
}

const DEFAULT_BACKOFF_MS: readonly number[] = [
  200, 500, 1000, 2000, 5000, 10000, 20000, 30000,
];
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 30_000;
const DEFAULT_HIDDEN_TAB_PAUSE_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * Custom event name dispatched on `window` whenever the SSE stream
 * receives any data chunk or keepalive comment. `useSSEHealth` listens
 * for this to detect >15 s of producer silence.
 *
 * detail: `{ url: string; timestamp: number }`
 */
export const SSE_ACTIVITY_EVENT = "sse:activity" as const;

function safeParse<T>(raw: string): T {
  if (raw === "") return "" as unknown as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}

function backoffDelay(
  attempt: number,
  schedule: readonly number[],
): number {
  const base =
    attempt < schedule.length
      ? schedule[attempt]!
      : MAX_BACKOFF_MS;
  // ±1s of jitter prevents synchronised retries from many tabs.
  return base + Math.floor(Math.random() * 1000);
}

export function useSSE<E extends string, T>(
  url: string,
  handlers: SSEHandlers<E, T>,
  options: UseSSEOptions = {},
): UseSSEResult {
  const {
    enabled = true,
    withCredentials = false,
    backoffMs = DEFAULT_BACKOFF_MS,
    heartbeatTimeoutMs = DEFAULT_HEARTBEAT_TIMEOUT_MS,
    hiddenTabPauseMs = DEFAULT_HIDDEN_TAB_PAUSE_MS,
    fetchImpl,
    onAttempt,
  } = options;

  const [status, setStatus] = useState<SSEStatus>("connecting");
  const [error, setError] = useState<Error | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);

  // Refs so the effect doesn't re-run when handler identities change
  // mid-render. The ref is read inside the listener so the latest
  // handlers are always used.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Latest options refs — kept stable so the effect dependency array is
  // small. Mutating these doesn't tear down the connection.
  const optionsRef = useRef({
    backoffMs,
    heartbeatTimeoutMs,
    hiddenTabPauseMs,
    fetchImpl,
    withCredentials,
    onAttempt,
  });
  optionsRef.current = {
    backoffMs,
    heartbeatTimeoutMs,
    hiddenTabPauseMs,
    fetchImpl,
    withCredentials,
    onAttempt,
  };

  // The retry trigger is exposed via the result; we bump a counter to
  // ask the effect to schedule a reconnect immediately.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setStatus("paused");
      return undefined;
    }
    // SSR / non-browser guard. `fetch` exists in Node 18+ but `window`
    // does not — and we wire visibilitychange via document.
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    let cancelled = false;
    let handle: FetchEventSourceHandle | null = null;
    let backoffTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenPauseTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let lastEventId = "";

    const clearBackoff = () => {
      if (backoffTimer !== null) {
        clearTimeout(backoffTimer);
        backoffTimer = null;
      }
    };
    const clearHeartbeat = () => {
      if (heartbeatTimer !== null) {
        clearTimeout(heartbeatTimer);
        heartbeatTimer = null;
      }
    };
    const clearHiddenPause = () => {
      if (hiddenPauseTimer !== null) {
        clearTimeout(hiddenPauseTimer);
        hiddenPauseTimer = null;
      }
    };

    const armHeartbeat = () => {
      clearHeartbeat();
      // Broadcast activity so `useSSEHealth` can reset its silence timer.
      window.dispatchEvent(
        new CustomEvent(SSE_ACTIVITY_EVENT, {
          detail: { url, timestamp: Date.now() },
        }),
      );
      heartbeatTimer = setTimeout(() => {
        // Producer has gone silent — force-reconnect.
        if (handle) {
          handle.close();
          handle = null;
        }
        setError(new Error("sse_heartbeat_timeout"));
        scheduleReconnect();
      }, optionsRef.current.heartbeatTimeoutMs);
    };

    const dispatchEvent = (event: PolyfillEvent) => {
      // Heartbeat refresh on every chunk; the polyfill calls `onChunk`
      // separately for that but events also count.
      armHeartbeat();
      if (event.lastEventId) lastEventId = event.lastEventId;
      if (event.isComment) return;
      const handler = handlersRef.current[event.type as E];
      if (!handler) return;
      try {
        handler(safeParse<T>(event.data), event);
      } catch {
        // A bad handler shouldn't tear down the stream.
      }
    };

    const openStream = () => {
      if (cancelled) return;
      optionsRef.current.onAttempt?.({
        url,
        lastEventId,
        attempt,
      });
      setStatus(attempt === 0 ? "connecting" : "reconnecting");
      armHeartbeat();
      handle = openFetchEventSource({
        url,
        lastEventId,
        withCredentials: optionsRef.current.withCredentials,
        fetchImpl: optionsRef.current.fetchImpl,
        onOpen: () => {
          if (cancelled) return;
          attempt = 0;
          setStatus("open");
          setError(null);
        },
        onChunk: () => {
          armHeartbeat();
        },
        onEvent: dispatchEvent,
        onClose: (err) => {
          if (cancelled) return;
          if (err) setError(err);
          handle = null;
          clearHeartbeat();
          scheduleReconnect();
        },
      });
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") {
        // We'll reopen when the tab returns to the foreground.
        setStatus("paused");
        return;
      }
      const delay = backoffDelay(attempt, optionsRef.current.backoffMs);
      attempt += 1;
      setReconnectCount((c) => c + 1);
      setStatus("reconnecting");
      clearBackoff();
      backoffTimer = setTimeout(openStream, delay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearHiddenPause();
        hiddenPauseTimer = setTimeout(() => {
          if (handle) {
            handle.close();
            handle = null;
          }
          clearHeartbeat();
          clearBackoff();
          setStatus("paused");
        }, optionsRef.current.hiddenTabPauseMs);
      } else {
        clearHiddenPause();
        if (!handle && status !== "open") {
          attempt = 0;
          openStream();
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    openStream();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearBackoff();
      clearHeartbeat();
      clearHiddenPause();
      if (handle) handle.close();
    };
    // `handlers` and option callbacks intentionally omitted — we read
    // them via refs. Reconnecting on every render would tear down the
    // stream constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, retryNonce]);

  const connected = status === "open";
  const reconnecting = status === "reconnecting";

  return {
    connected,
    error,
    reconnecting,
    status,
    reconnectCount,
    retry: () => setRetryNonce((n) => n + 1),
  };
}

/**
 * Test-only: parse a raw `event.data` payload the same way `useSSE`
 * does. Exported so unit tests of producers can verify the wire
 * round-trip without standing up a full EventSource.
 */
export function _parseSSEDataForTests<T>(raw: string): T {
  return safeParse<T>(raw);
}

// ── Ingest health hook ────────────────────────────────────────────────────────

/** Shape broadcast by the ingest watchdog every 30 s. */
export interface IngestJobHealth {
  name: string;
  intervalMs: number;
  lastRunAt: string | null; // ISO-8601 or null
  lastStatus: "ok" | "error" | "never";
  lastDurationMs: number | null;
}

export interface IngestHealthSnapshot {
  jobs: IngestJobHealth[];
  isHealthy: boolean;
  checkedAt: string; // ISO-8601
}

export type UseIngestHealthOptions = UseSSEOptions;

/**
 * Subscribe to the `/sse/ingest/health` stream and call `onUpdate`
 * whenever the watchdog broadcasts a new snapshot (every ~30 s).
 *
 * ```tsx
 * const { connected } = useIngestHealth(apiUrl, (snap) => {
 *   if (!snap.isHealthy) showStaleDataBanner();
 * });
 * ```
 */
export function useIngestHealth(
  apiUrl: string,
  onUpdate: (snapshot: IngestHealthSnapshot) => void,
  options: UseIngestHealthOptions = {},
): UseSSEResult {
  const url = `${apiUrl}/sse/ingest/health`;
  return useSSE<"system:ingest-health", IngestHealthSnapshot>(
    url,
    { "system:ingest-health": onUpdate },
    options,
  );
}
